import * as React from 'react'
import { bind } from 'classnames/bind'
import * as styles from '@procore/core-css/src/components/_banner.scss'

import addSubcomponents from '../utils/addSubcomponents'
import { Props, SizeNS, DivAttributes } from '../utils/types'
import BaseIcon from './Icon'

export namespace BannerVariantNS {
  export const action = 'action'
  export const error = 'error'
  export const info = 'info'
  export const success = 'success'
}

export namespace BannerIconVariantNS {
  export const action = 'warning-triangle-o'
  export const error = 'warning-triangle-o'
  export const info = 'info-circle-o'
  export const success = 'check-circle'
}

type BannerVariant =
  | typeof BannerVariantNS.action
  | typeof BannerVariantNS.error
  | typeof BannerVariantNS.info
  | typeof BannerVariantNS.success

type BannerIconVariant =
  | typeof BannerIconVariantNS.action
  | typeof BannerIconVariantNS.error
  | typeof BannerIconVariantNS.info
  | typeof BannerIconVariantNS.success

interface BannerProps extends Props {
  variant: BannerVariant
}

interface BannerIconProps extends Props {
  icon: BannerIconVariant
}

const cx = bind(styles)

export const Banner = React.forwardRef<
  HTMLDivElement,
  DivAttributes & BannerProps
>(({ children, className, variant, ...props }, ref) => (
  <div
    ref={ref}
    className={cx(
      'core-banner',
      { [`core-banner--${variant}`]: variant },
      className
    )}
    {...props}
  >
    {children}
  </div>
))

export const Content = React.forwardRef<HTMLDivElement, DivAttributes & Props>(
  ({ children, className, ...props }, ref) => (
    <div ref={ref} className={cx('core-banner__content', className)} {...props}>
      {children}
    </div>
  )
)

export const Title = React.forwardRef<HTMLDivElement, DivAttributes & Props>(
  ({ children, className, ...props }, ref) => (
    <div ref={ref} className={cx('core-banner__title', className)} {...props}>
      {children}
    </div>
  )
)

export const Body = React.forwardRef<HTMLDivElement, DivAttributes & Props>(
  ({ children, className, ...props }, ref) => (
    <div ref={ref} className={cx('core-banner__body', className)} {...props}>
      {children}
    </div>
  )
)

export const Action = React.forwardRef<HTMLDivElement, DivAttributes & Props>(
  ({ children, className, ...props }, ref) => (
    <div ref={ref} className={cx('core-banner__action', className)} {...props}>
      {children}
    </div>
  )
)

export const Icon = React.forwardRef<
  HTMLDivElement,
  DivAttributes & BannerIconProps
>(({ children, className, icon, ...props }, ref) => (
  <div
    ref={ref}
    className={cx('core-banner__icon', className)}
    aria-hidden="true"
    {...props}
  >
    <BaseIcon icon={icon} size={SizeNS.md} />
  </div>
))

export const Dismiss = React.forwardRef<HTMLButtonElement, Props>(
  ({ className, children, ...props }, ref) => (
    <button
      ref={ref}
      className={cx('core-banner__dismiss', className)}
      {...props}
    >
      <BaseIcon icon="x" />
    </button>
  )
)

export const ErrorBanner = React.forwardRef<
  HTMLDivElement,
  DivAttributes & Props
>(({ children, ...props }, ref) => (
  <Banner ref={ref} variant={BannerVariantNS.error} {...props}>
    <Icon icon={BannerIconVariantNS.error} />
    {children}
  </Banner>
))

export const SuccessBanner = React.forwardRef<
  HTMLDivElement,
  DivAttributes & Props
>(({ children, ...props }, ref) => (
  <Banner ref={ref} variant={BannerVariantNS.success} {...props}>
    <Icon icon={BannerIconVariantNS.success} />
    {children}
  </Banner>
))

export const InfoBanner = React.forwardRef<
  HTMLDivElement,
  DivAttributes & Props
>(({ children, ...props }, ref) => (
  <Banner ref={ref} variant={BannerVariantNS.info} {...props}>
    <Icon icon={BannerIconVariantNS.info} />
    {children}
  </Banner>
))

export const ActionBanner = React.forwardRef<
  HTMLDivElement,
  DivAttributes & Props
>(({ children, ...props }, ref) => (
  <Banner ref={ref} variant={BannerVariantNS.action} {...props}>
    <Icon icon={BannerIconVariantNS.action} />
    {children}
  </Banner>
))

Banner.displayName = 'Banner'

Body.displayName = 'Banner.Body'

Content.displayName = 'Banner.Content'

Title.displayName = 'Banner.Title'

Action.displayName = 'Banner.Action'

Icon.displayName = 'Banner.Icon'

Dismiss.displayName = 'Banner.Dismiss'

ErrorBanner.displayName = 'ErrorBanner'

SuccessBanner.displayName = 'SuccessBanner'

InfoBanner.displayName = 'InfoBanner'

ActionBanner.displayName = 'ActionBanner'

export default addSubcomponents(
  {
    Banner,
    Body,
    Content,
    Title,
    Action,
    Icon,
    Dismiss,
  },
  Banner
)
